import * as React from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cargofl-ui';

/**
 * "Are you sure" for the actions that take effect the instant they are clicked.
 *
 * The bar for putting one of these in front of an action is narrow on purpose, because
 * a confirm on everything is a confirm nobody reads. It is worth it when the action is
 * **immediate and not undone by clicking again**: freezing a quarter shuts every stage,
 * publishing goals mails the whole selection, forcing a window open changes who may
 * submit. Undoing those is a second deliberate act, sometimes by a different person,
 * and every one of them is audited under the name of whoever mis-clicked.
 *
 * It is NOT worth it for anything reversible in place - ticking a checkbox, saving a
 * draft, renaming a department - or for anything that already opens a dialog to collect
 * a reason, which is its own confirmation step.
 *
 * The description says what will happen in the world, not what the button is called.
 * "This cannot be undone" is not a description; "17 employees will be emailed that
 * their goals are live" is.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = false,
  pending = false,
  onConfirm,
  children,
  confirmDisabled = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  /** Red confirm button. For actions that remove or shut something. */
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  /** An input the action needs (e.g. an effective date), between the text and the buttons. */
  children?: React.ReactNode;
  /** Holds Confirm shut until the children are satisfied (e.g. typed confirmation). */
  confirmDisabled?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          {/* Cancel first and Confirm last, and Cancel is the plain one: the safe
              choice should never be the one styled to be pressed. */}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A button that asks first.
 *
 * The common shape: one button, one action, one question. Where the trigger is not a
 * button — a dropdown selection that fires immediately, say — drive `ConfirmDialog`
 * directly from state instead.
 */
export function ConfirmButton({
  title,
  description,
  confirmLabel,
  destructive,
  pending,
  onConfirm,
  children,
  ...buttonProps
}: {
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof Button>, 'onClick'>) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Button {...buttonProps} onClick={() => setOpen(true)}>
        {children}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        confirmLabel={confirmLabel}
        destructive={destructive}
        pending={pending}
        onConfirm={() => {
          // Closed before the work starts, not after: these actions all report their
          // own outcome in a toast, and a dialog sitting open behind a toast reads as
          // "it did not take".
          setOpen(false);
          onConfirm();
        }}
      />
    </>
  );
}
